import * as Y from 'yjs';

import { UpdateMessageOrigin } from '@canvas/protocol-types';
import {
  CanvasTransactionOrigin,
  IMMEDIATE_ORIGINS,
  type TransactionOrigin,
  YJS_DEFAULT,
  type Transaction,
  type YjsOrigin as YjsOriginInterface,
} from '@canvas/transaction-types';

const CanvasTransactionOriginValues = new Set(Object.values(CanvasTransactionOrigin));

export const isTransactionOrigin = (val: any): val is TransactionOrigin => {
  return val === YJS_DEFAULT || val === undefined || isCanvasTransactionOrigin(val);
};

const isUndoManager = (origin: TransactionOrigin) => {
  return (
    origin instanceof Y.UndoManager ||
    CanvasTransactionOrigin.RemoteUndoManager === origin
  );
};

const isResizing = (origin: TransactionOrigin) => {
  return CanvasTransactionOrigin.Resizing === origin;
};

const isPrivateSessionManager = (origin: TransactionOrigin) => {
  return CanvasTransactionOrigin.PrivateSessionManager === origin;
};

const isAgent = (origin: TransactionOrigin) => {
  return (
    CanvasTransactionOrigin.Agent === origin ||
    CanvasTransactionOrigin.AgentSkipLocalObservers === origin ||
    CanvasTransactionOrigin.AgentImmediate === origin
  );
};

const isBackendAgent = (origin: TransactionOrigin) => {
  return CanvasTransactionOrigin.BackendAgent === origin;
};

const isCanvasTransactionOrigin = (origin: string): origin is CanvasTransactionOrigin => {
  return CanvasTransactionOriginValues.has(origin as CanvasTransactionOrigin);
};
export const originIsUndoManager = (origin: TransactionOrigin) => {
  const isYjsOriginUndoManager =
    origin instanceof YjsOrigin &&
    (isUndoManager(origin.remoteOrigin) || isUndoManager(origin.localOrigin));
  return isUndoManager(origin) || isYjsOriginUndoManager;
};

export const originIsCleanDocument = (origin: TransactionOrigin) => {
  return CanvasTransactionOrigin.CleanDocument === origin;
};

export const originIsResizing = (origin: TransactionOrigin) => {
  const isYjsOriginResizing =
    origin instanceof YjsOrigin &&
    (isResizing(origin.remoteOrigin) || isResizing(origin.localOrigin));
  return isResizing(origin) || isYjsOriginResizing;
};

/**
 * Whether the origin represents agent work started in the browser. `BackendAgent`
 * origins are classified separately by `originIsBackendAgent`.
 */
export const originIsAgent = (origin: TransactionOrigin) => {
  return isAgent(origin);
};

export const originIsBackendAgent = (origin: TransactionOrigin) => {
  const isYjsOriginBackendAgent =
    origin instanceof YjsOrigin &&
    (isBackendAgent(origin.remoteOrigin) || isBackendAgent(origin.localOrigin));
  return isBackendAgent(origin) || isYjsOriginBackendAgent;
};

const isImmediate = (origin: TransactionOrigin) => {
  return (
    typeof origin === 'string' && IMMEDIATE_ORIGINS.has(origin as CanvasTransactionOrigin)
  );
};

export const originIsImmediate = (origin: TransactionOrigin) => {
  const isYjsOriginImmediate =
    origin instanceof YjsOrigin &&
    (isImmediate(origin.remoteOrigin) || isImmediate(origin.localOrigin));
  return isImmediate(origin) || isYjsOriginImmediate;
};

const isLayoutShift = (origin: TransactionOrigin) => {
  return CanvasTransactionOrigin.LayoutShift === origin;
};

export const originIsLayoutShift = (origin: TransactionOrigin) => {
  const isYjsOriginLayoutShift =
    origin instanceof YjsOrigin &&
    (isLayoutShift(origin.remoteOrigin) || isLayoutShift(origin.localOrigin));
  return isLayoutShift(origin) || isYjsOriginLayoutShift;
};

export function getUpdateMessageOrigin(origin: TransactionOrigin): UpdateMessageOrigin {
  let flag = UpdateMessageOrigin.None;

  if (originIsUndoManager(origin)) {
    flag |= UpdateMessageOrigin.UndoRedo;
  } else if (originIsCleanDocument(origin)) {
    flag |= UpdateMessageOrigin.CleanDocument;
  } else if (originIsResizing(origin)) {
    flag |= UpdateMessageOrigin.Resizing;
  } else if (originIsBackendAgent(origin)) {
    flag |= UpdateMessageOrigin.BackendAgent;
  } else if (originIsAgent(origin)) {
    flag |= UpdateMessageOrigin.Agent;
  } else if (originIsLayoutShift(origin)) {
    flag |= UpdateMessageOrigin.LayoutShift;
  }

  return flag;
}

export const originatesFromUndoManager = (transaction: Transaction) =>
  originIsUndoManager(transaction.origin);

export const originatesFromPrivateSessionManager = (transaction: Transaction) =>
  isPrivateSessionManager(transaction.origin);

export const originatesFromAgent = (transaction: Transaction) =>
  isAgent(transaction.origin);

export const originatesFromRemoteBackendAgent = (transaction: Transaction) =>
  !transaction.local &&
  transaction.origin instanceof YjsOrigin &&
  originIsBackendAgent(transaction.origin.remoteOrigin);

export class YjsOrigin implements YjsOriginInterface {
  constructor(
    public readonly remoteOrigin: TransactionOrigin,
    public readonly localOrigin: TransactionOrigin,
  ) {}
}

export class BackendAgentOrigin extends YjsOrigin {
  public readonly agentOwnerAccountId?: string;
  public readonly agentOperationId?: string;

  constructor(
    localOrigin: TransactionOrigin,
    {
      agentOwnerAccountId,
      agentOperationId,
    }: {
      agentOwnerAccountId?: string;
      agentOperationId?: string;
    } = {},
  ) {
    super(CanvasTransactionOrigin.BackendAgent, localOrigin);
    this.agentOwnerAccountId = agentOwnerAccountId;
    this.agentOperationId = agentOperationId;
  }
}

export { CanvasTransactionOrigin, YJS_DEFAULT } from '@canvas/transaction-types';

export { TransactionOrigin, Transaction };
